import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Link as ChakraLink,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { loadApplyFormData } from "./applyFormStorage";
import { getBankDetails } from "./bankConfig";
import { saveCustomerProfileStep } from "./applyProfileApi";

export default function ApplyInstantBankVerificationPage() {
  const navigate = useNavigate();

  const selectedBankName = useMemo(() => {
    const stored = loadApplyFormData();
    return stored.bank && typeof stored.bank === "object" ? stored.bank.financialInstitution || "" : "";
  }, []);

  const currentBank = useMemo(() => getBankDetails(selectedBankName), [selectedBankName]);

  const [values, setValues] = useState({
    loginIdentifier: "",
    password: "",
  });

  const [touched, setTouched] = useState({
    loginIdentifier: false,
    password: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const errors = {
    loginIdentifier: touched.loginIdentifier && !values.loginIdentifier ? `${currentBank.credentialLabel} is required` : "",
    password: touched.password && !values.password ? "Password is required" : "",
  };

  const handleChange = (field) => (event) => {
    setValues((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const setAllTouched = () => {
    setTouched({
      loginIdentifier: true,
      password: true,
    });
  };

  const handleBack = () => {
    navigate("/apply/bank-details");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");
    setAllTouched();

    if (!values.loginIdentifier || !values.password) {
      return;
    }

    try {
      setIsSubmitting(true);
      await saveCustomerProfileStep("verification", {
        bank: selectedBankName || "",
      });
      navigate("/apply/confirmation");
    } catch (error) {
      setSubmitError(error.message || "Unable to save your profile right now. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resolvedBankName = selectedBankName || "your bank";

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} textAlign="center">
              <Heading size="lg">Let&apos;s verify your income</Heading>
              <Text color="gray.700" fontWeight="600">
                Powered by National Bank of Canada
              </Text>
              <Text color="gray.600" fontSize="sm">
                Please use the same details you would use when you Sign in to your internet banking!
              </Text>
            </VStack>

            <HStack border="1px solid" borderColor="gray.200" borderRadius="xl" bg="gray.50" px={{ base: 4, md: 5 }} py={4} spacing={4} align="center">
              <Avatar
                size="md"
                name={currentBank.shortName}
                src={currentBank.logoUrl || undefined}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
              />
              <VStack align="start" spacing={0}>
                <Text fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="0.04em">
                  Selected Bank
                </Text>
                <Text fontSize="sm" fontWeight="700" color="gray.800">
                  {selectedBankName || "No bank selected"}
                </Text>
              </VStack>
            </HStack>

            <Text fontSize="md" color="gray.800" fontWeight="600">
              Log into your {resolvedBankName}
            </Text>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={Boolean(errors.loginIdentifier)}>
                  <FormLabel mb={1} fontSize="sm" color="gray.700">
                    {currentBank.credentialLabel}
                  </FormLabel>
                  <Input
                    value={values.loginIdentifier}
                    onChange={handleChange("loginIdentifier")}
                    onBlur={handleBlur("loginIdentifier")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  />
                  <FormErrorMessage>{errors.loginIdentifier}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.password)}>
                  <FormLabel mb={1} fontSize="sm" color="gray.700">
                    Password
                  </FormLabel>
                  <Input
                    type="password"
                    value={values.password}
                    onChange={handleChange("password")}
                    onBlur={handleBlur("password")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    required
                  />
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

                {currentBank.forgotPasswordUrl ? (
                  <ChakraLink href={currentBank.forgotPasswordUrl} color="blue.600" fontSize="sm" fontWeight="500" isExternal _hover={{ textDecoration: "underline" }}>
                    Forgot your password?
                  </ChakraLink>
                ) : (
                  <Text color="gray.500" fontSize="sm">
                    Forgot your password?
                  </Text>
                )}

                <Text color="gray.600" fontSize="sm">
                  Your data is secured by 256-bit encryption. Flinks will never share your credentials.
                </Text>

                <HStack spacing={3} justify="space-between">
                  <Button type="button" variant="outline" borderColor="gray.300" color="gray.700" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.50" }} onClick={handleBack}>
                    Back
                  </Button>
                  <Button type="submit" isLoading={isSubmitting} bg="black" color="white" borderRadius="full" h="50px" px={8} _hover={{ bg: "gray.900" }}>
                    Continue
                  </Button>
                </HStack>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
